import { Lang, Role, Trade } from "@gigsync/protocol";
import { ulid } from "ulid";
import { newEngagement } from "./engagement/machine.js";
import { BULLET_MARK, plainReply } from "./model-reply.js";
import { Collection } from "./ports.js";
import { choicesOf } from "./router/clarify.js";
import { Expert } from "./router/keyword.js";
import { recentLines } from "./router/model-prompt.js";
export const CardKind = {
    text: "text",
    saved: "saved",
    answer: "answer",
    draft: "draft",
    needsModel: "needs_model",
    clarify: "clarify",
    /** The worker's own schedule (ADR 0048): a line, then bookings and signed jobs the phone writes in its language. */
    schedule: "schedule",
};
/** The question for a message the model could not place, offering the intents it was torn between. */
export const clarifyCard = (unsure) => ({
    kind: CardKind.clarify,
    say: { key: "agent.clarify" },
    choices: choicesOf(unsure),
});
/** The language the on-device models answer in today (HLD 7.6); their replies are translated on the phone. */
const MODEL_LANG = Lang.enum.en;
/** Two short sentences and a little room: a reply the owner reads at a glance and hears in a breath. */
const REPLY_MAX_TOKENS = 120;
const LINE = "\n";
/** Engagements the agent opens from a chat question. */
const QUESTION_TOPIC = "question";
/** The card for a route; `recent` is the chat so far, which a free reply is written against. */
export async function runExpert(route, ports, contacts, recent) {
    const now = ports.clock.now();
    switch (route.expert) {
        case Expert.remember: {
            const episode = { id: `ep_${ulid(now.getTime())}`, text: route.text, created_at: now.toISOString() };
            await ports.storage.put(Collection.episodes, episode.id, episode);
            return { kind: CardKind.saved, say: { key: "agent.saved", params: { text: route.text } }, episode: episode.id };
        }
        case Expert.askSelf: {
            const episodes = await ports.storage.list(Collection.episodes);
            const hits = search(route.text, episodes).slice(0, 3);
            if (hits.length === 0)
                return { kind: CardKind.answer, say: { key: "agent.nothing" }, sources: [] };
            const answered = await ports.model.generate(answerPrompt(route.text, hits), { maxTokens: REPLY_MAX_TOKENS });
            const draft = answered && plainReply(answered);
            // Grounding rule: with no model we quote the notes; with one we still show which notes it used.
            const words = draft
                ? { text: draft, lang: MODEL_LANG }
                : { text: hits.map((h) => `${BULLET_MARK}${h.text}`).join(LINE) };
            return { kind: CardKind.answer, words, sources: hits.map((h) => h.id) };
        }
        case Expert.askContact: {
            const contact = contacts.find((c) => c.name.toLowerCase() === route.contact?.toLowerCase());
            if (!contact)
                return { kind: CardKind.text, say: { key: "agent.no_contact", params: { name: route.contact ?? "" } } };
            const engagement = newEngagement({
                role: Role.enum.requester,
                counterpart: contact.id,
                brief: { text: route.text, topic: QUESTION_TOPIC, shared: {} },
            }, now);
            await ports.storage.put(Collection.engagements, engagement.id, engagement);
            return {
                kind: CardKind.draft,
                say: { key: "agent.ask_contact", params: { name: contact.name, text: route.text } },
                engagement,
            };
        }
        case Expert.setRule:
            return { kind: CardKind.text, say: { key: "agent.rules_later", params: { text: route.text } } };
        case Expert.find:
            return { kind: CardKind.text, say: { key: "agent.market_later", params: { text: route.text } } };
        default: {
            const answered = await ports.model.generate(chatPrompt(route.text, recent), { maxTokens: REPLY_MAX_TOKENS });
            const reply = answered && plainReply(answered);
            return reply
                ? { kind: CardKind.text, words: { text: reply, lang: MODEL_LANG } }
                : { kind: CardKind.needsModel, say: { key: "agent.needs_model" } };
        }
    }
}
/** Splits on anything that is not a letter, a combining mark or a digit, so Devanagari and Kannada words stay whole. */
const NOT_WORD = /[^\p{L}\p{M}\p{N}]+/u;
const MIN_WORD = 3;
function search(query, episodes) {
    const words = query
        .toLowerCase()
        .split(NOT_WORD)
        .filter((w) => w.length >= MIN_WORD);
    return episodes
        .map((e) => ({ e, score: words.filter((w) => e.text.toLowerCase().includes(w)).length }))
        .filter((x) => x.score > 0)
        .sort((a, b) => b.score - a.score || b.e.created_at.localeCompare(a.e.created_at))
        .map((x) => x.e);
}
/** How every free reply is asked for: the chat shows it as typed, the phone translates it and may read it aloud. */
const REPLY_RULES = "Reply in plain text, with no Markdown, in at most two short sentences.";
const UNDERSCORE = /_/g;
/** The kinds of work GigSync lists, as words: a small model otherwise fills in a trade from anywhere. */
const TRADES = Trade.options.map((trade) => trade.replace(UNDERSCORE, " ")).join(", ");
function answerPrompt(question, notes) {
    return [
        "Answer the owner's question using only these notes. If the notes do not answer it, say you don't know.",
        REPLY_RULES,
        ...notes.map((n, i) => `Note ${i + 1}: ${n.text}`),
        `Question: ${question}`,
    ].join(LINE);
}
/**
 * The chat's prompt: who is answering, the work GigSync knows, what it cannot do, how short, and the chat so far;
 * never the owner's words alone.
 */
function chatPrompt(message, recent) {
    return [
        "You are the owner's own GigSync agent, on their phone in Bengaluru.",
        `GigSync connects households with people who do home work: ${TRADES}.`,
        "Answer the message warmly and only what it asks. If you do not know, say so: never make up jobs, prices, people or kinds of work.",
        "You only talk: you cannot book, arrange, send or agree to anything, so never say you did or will.",
        REPLY_RULES,
        ...recentLines(recent),
        `Message: ${JSON.stringify(message)}`,
    ].join(LINE);
}
//# sourceMappingURL=experts.js.map